import React, { useEffect, useState } from 'react'
import DashboardWrapper from "../../layout/index";
import BarChart from '../../components/BarChart';
import { getB2BMatrics } from '../../api/dashobard';
import { useTranslation } from "react-i18next";
import Filter from "../../assets/icons/Filter.svg";
import Close from "../../assets/close-circle.svg";
import Loader from "../../components/Loader";
import Dropdown from "../../components/Dropdown";
import DropdownMulti from "../../components/DropdownMultiple";
import { useDateRangeSelector } from "../../components/DateRangeSelector";
import { formatDate, formatNumber, formatNumberWithoutFraction } from "../../utility";
import TimeFrameSelector from "../../components/TimeFrameSelector";
import { formatCurrentEndDate, formatCurrentStartDate } from "../../utility";
import { getGlobalFilter } from "../../api/filters";
import GuageChart from "../../components/GuageChart";
import PieChart from "../../components/PieChart";
import { preseller } from '../../utility/constant';

const B2BDashboard = () => {
  const [formattedAalytic, setFormateedAnanlytic] = useState<any>([])
  const [timeFrame, setSelectTimeFrame] = useState<any>("Today");
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState<any | undefined>(getCurrentDate());
  const [endDate, setEndDate] = useState<any | undefined>(getCurrentDate());
  const [open, setOpen] = useState(false);
  const [loadingFilter, setLoadingFilter] = useState<boolean>(false);
  const [globalFilters, setGlobalFilters] = useState<any>(null);
  const [salesOffice, setSelectedSalesOffice] = useState<any>("");
  const [salesRegion, setSelectedSalesRegion] = useState<any>([]);
  const [orderMetrics, setOrderMetrics] = useState<any>(null);
  const [dateMode, setDateMode] = useState<"picker" | "month">("picker");
  const dateRangeSelector = useDateRangeSelector(2024);

  // Exclude specific channels globally in this dashboard
  const isExcludedChannel = (name: string) => {
    const normalized = (name || "").toLowerCase().trim();
    return normalized === "free of charge" || normalized === "berain export";
  };

  const handleClearFilters = () => {
    const defaultStartDate = getCurrentDate();
    const defaultEndDate = getCurrentDate();

    dateRangeSelector.reset();
    setDateMode("picker");
    setStartDate(defaultStartDate);
    setEndDate(defaultEndDate);
    setSelectTimeFrame("Today");
    handleClearApply(defaultStartDate, defaultEndDate);
  };

  const handleClearApply = (startDate: string, endDate: string) => {
      setOpen(false);
  
      const data: any = {};
      if (startDate) {
        data["start_date"] = formatCurrentStartDate(new Date());
      }
      if (endDate) {
        data["end_date"] = formatCurrentEndDate(new Date());
      }
      data["positions"]=[preseller]
      getAnalyticsData(data);
    };

  function getCurrentDate() {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value);
    checkTimeFrame(startDate, e.target.value);
  };

  const applyYearMonth = (yearVal: number, monthVal: number) => {
    if (!yearVal || !monthVal) return;
    const { start, end } = dateRangeSelector.getMonthRange(yearVal, monthVal);
    setStartDate(formatDate(start));
    setEndDate(formatDate(end));
    setDateMode("month");
  };

  const handleMonthYearDropdownChange = (value: string | number) => {
    const yearNum = Number(value);
    dateRangeSelector.setSelectedMonthYear(yearNum);
    if (dateRangeSelector.selectedMonth) {
      const monthNum = Number(dateRangeSelector.selectedMonth);
      if (yearNum === dateRangeSelector.currentYear && monthNum > dateRangeSelector.currentMonth) {
        dateRangeSelector.setSelectedMonth("");
      } else {
        applyYearMonth(yearNum, monthNum);
      }
    }
  };

  const handleMonthDropdownChange = (value: string | number) => {
    const monthNum = Number(value);
    dateRangeSelector.setSelectedMonth(String(monthNum));
    if (dateRangeSelector.selectedMonthYear) {
      applyYearMonth(Number(dateRangeSelector.selectedMonthYear), monthNum);
    }
  };

  const getAnalyticsData = async (param: any) => {
    setFormateedAnanlytic([])
    await getB2BMatrics(param).then((res) => {
      // Set order metrics
      setOrderMetrics(res.data);
      
      let dataArray: any = [];
      
      // Handle targets data
      res.data?.targets.forEach((item: any) => {
        const poMetrics = res.data?.po_metrics.find((po: any) => 
          po.region === item.region && 
          po.sales_office_id === item.sales_office_id
        ) || {
          confirmed_cartons_qty: 0,
          cancelled_cartons_qty: 0,
          delivered_cartons_qty: 0,
          confirmed_po_count: 0,
          cancelled_po_count: 0,
          delivered_po_count: 0
        };

        dataArray?.push({
          region: item.region || 'All Regions',
          data: [{
            visits: "Acheived",
            value: formatNumberWithoutFraction(Math.round(item.achieved_target)) || 0,
          },
          {
            visits: "Target",
            value: formatNumberWithoutFraction(Math.round(item.target)) || 0,
          }],
          po_metrics: {
            confirmed_cartons_qty: formatNumberWithoutFraction(Math.round(poMetrics.confirmed_cartons_qty)) || 0,
            cancelled_cartons_qty: formatNumberWithoutFraction(Math.round(poMetrics.cancelled_cartons_qty)) || 0,
            delivered_cartons_qty: formatNumberWithoutFraction(Math.round(poMetrics.delivered_cartons_qty)) || 0,
            confirmed_po_count: formatNumberWithoutFraction(Math.round(poMetrics.confirmed_po_count)) || 0,
            cancelled_po_count: formatNumberWithoutFraction(Math.round(poMetrics.cancelled_po_count)) || 0,
            delivered_po_count: formatNumberWithoutFraction(Math.round(poMetrics.delivered_po_count)) || 0
          }
        });
      });

      // If no targets data but PO metrics exist, create entries from PO metrics
      if (dataArray.length === 0 && res.data?.po_metrics?.length > 0) {
        res.data.po_metrics.forEach((po: any) => {
          dataArray.push({
            region: po.region || 'All Regions',
            data: [{
              visits: "Acheived",
              value: 0,
            },
            {
              visits: "Target",
              value: 0,
            }],
            po_metrics: {
              confirmed_cartons_qty: formatNumberWithoutFraction(Math.round(po.confirmed_cartons_qty)) || 0,
              cancelled_cartons_qty: formatNumberWithoutFraction(Math.round(po.cancelled_cartons_qty)) || 0,
              delivered_cartons_qty: formatNumberWithoutFraction(Math.round(po.delivered_cartons_qty)) || 0,
              confirmed_po_count: formatNumberWithoutFraction(Math.round(po.confirmed_po_count)) || 0,
              cancelled_po_count: formatNumberWithoutFraction(Math.round(po.cancelled_po_count)) || 0,
              delivered_po_count: formatNumberWithoutFraction(Math.round(po.delivered_po_count)) || 0
            }
          });
        });
      }

      setFormateedAnanlytic(dataArray);
    }).catch(() => {
      // Handle error case
    });
  }

  const checkTimeFrame = (start: any, end: any) => {
    if (start === formatDate(new Date()) && end === formatDate(new Date())) {
      setSelectTimeFrame("Today");
    } else if (
      start === formatDate(new Date().setDate(new Date().getDate() - 1)) &&
      end === formatDate(new Date().setDate(new Date().getDate() - 1))
    ) {
      setSelectTimeFrame("Yesterday");
    } else if (
      start === formatDate(new Date().setDate(1)) &&
      end === formatDate(new Date())
    ) {
      setSelectTimeFrame("Month Till Date");
    } else {
      setSelectTimeFrame("");
    }
  };

  const getGlobalFilters = async (data: any) => {
    try {
      if (loadingFilter) {
        return;
      }
      setLoadingFilter(true);
      const response = await getGlobalFilter(data);
      setGlobalFilters(response);
    } catch (error) {
      console.error("Error fetching global filters:", error);
    } finally {
      setLoadingFilter(false);
    }
  };

   const handleFiltersUpdate = () => {
      const data: any = {};
  
      if (salesRegion.length > 0) {
        data["regions"] = salesRegion;
      }
      if (salesOffice.length > 0) {
        data["sales_office_ids"] = salesOffice;
      }
  
      data["endpoint"] = "visit-summary";
      data["start_date"] = formatCurrentStartDate(new Date(startDate));
      data["end_date"] = formatCurrentEndDate(new Date(endDate));
      data["positions"]=[]
      getGlobalFilters(data);
    };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(e.target.value);
    checkTimeFrame(e.target.value, endDate);
  };

  const handleApply = () => {
    setOpen(false);
    const data: any = {};
    if (startDate) {
      data["start_date"] = formatCurrentStartDate(new Date(startDate));
    }
    if (startDate) {
      data["end_date"] = formatCurrentEndDate(new Date(endDate));
    }
    if (startDate) {
      data["group_by_sales_office"] = false;
    }
    if (salesRegion.length > 0) {
      data["regions"] = salesRegion;
    }
    if (salesOffice) {
      data["sales_office_ids"] = salesOffice;
    }
data["positions"]=[]
    getAnalyticsData(data);

  };
  const filteredChannelsWiseOrders = orderMetrics?.customer_metrics?.channels_wise_orders?.filter(
    (channel: any) => !isExcludedChannel(channel?.channel_name)
  ) || [];

  const dataForBarChartOrders = filteredChannelsWiseOrders.map((channel: any) => ({
    visits: channel.channel_id,
    value: channel.count || 0,
  }));

  const dataForBarChartCartons = filteredChannelsWiseOrders.map((channel: any) => ({
    visits: channel.channel_id,
    value: channel.total_carton_qty || 0,
  }));
  const getPieChartData = () => {
    if (!orderMetrics) return { countData: [], quantityData: [] };
  
    const countData = [
      {
        name: 'Confirmed Orders',
        value: orderMetrics?.orders_metrics.confirmed_orders.count,
        color: '#00C48C',
        id: 'Confirmed Orders'
      },
      {
        name: 'Delivered Orders',
        value: orderMetrics?.orders_metrics.delivered_orders.count,
        color: '#408BFC',
        id: 'Delivered Orders'
      },
            {
        name: 'Shipped Orders',
        value: orderMetrics?.orders_metrics.shipped_orders.count,
        color: '#b84dff',
        id: 'Shipped Orders',
      },
      {
        name: 'Cancelled Orders',
        value: orderMetrics?.orders_metrics.cancelled_orders.count,
        color: '#FF4D68',
        id: 'Cancelled Orders',
      }
    ];
  
    const quantityData = [
      {
        name: 'Confirmed Quantity',
        value: orderMetrics?.orders_metrics.confirmed_orders.product_quantity,
        color: '#00C48C',
        id: 'Confirmed Quantity',
      },
      {
        name: 'Delivered Quantity',
        value: orderMetrics?.orders_metrics.delivered_orders.product_quantity,
        color: '#408BFC',
        id: 'Delivered Quantity'
      },
                  {
        name: 'Shipped Quantity',
        value: orderMetrics?.orders_metrics.shipped_orders.product_quantity,
        color: '#b84dff',
        id: 'Shipped Quantity',
      },
      {
        name: 'Cancelled Quantity',
        value: orderMetrics?.orders_metrics.cancelled_orders.product_quantity,
        color: '#FF4D68',
        id: 'Cancelled Quantity'
      }
    ];
  
    return { countData, quantityData };
  };

  const getOrderTypesChartData = () => {
    if (!orderMetrics) return [];
    
    const creditCount = orderMetrics?.orders_metrics?.credit_orders?.count || 0;
    const codCount = orderMetrics?.orders_metrics?.cod_orders?.count || 0;
    const paidCount = orderMetrics?.orders_metrics?.paid_orders?.count || 0;
    const pendingPaymentCount = orderMetrics?.orders_metrics?.pending_payment_orders?.count || 0;

    return [
      {
        name: 'Credit Orders',
        value: creditCount,
        color: '#408BFC',
        id: 'credit_orders'
      },
      {
        name: 'COD Orders',
        value: codCount,
        color: '#00C48C',
        id: 'cod_orders'
      },
      {
        name: 'Paid Orders',
        value: paidCount,
        color: '#b84dff',
        id: 'paid_orders'
      },
      // {
      //   name: 'Pending Payment Orders',
      //   value: pendingPaymentCount,
      //   color: '#FF4D68',
      //   id: 'pending_payment_orders'
      // }
    ];
  };

  useEffect(() => {

    const data: any = {};

    if (startDate) {
      data["start_date"] = formatCurrentStartDate(new Date(startDate));
    }
    if (startDate) {
      data["end_date"] = formatCurrentEndDate(new Date(endDate));
    }
    if (startDate) {
      data["group_by_sales_office"] = false;
    }
    if (salesRegion.length > 0) {
      data["regions"] = salesRegion;
    }
    if (salesOffice) {
      data["sales_office_ids"] = salesOffice;
    }
    getAnalyticsData(data)
  }, [])


useEffect(() => {
    const data: any = {};

    if (salesRegion.length > 0) {
      data["regions"] = salesRegion;
    }
    if (salesOffice) {
      data["sales_office_ids"] = salesOffice;
    }

    data["endpoint"] = "dashboard";
    data["start_date"] = formatCurrentStartDate(new Date(startDate));
    data["end_date"] = formatCurrentEndDate(new Date(endDate));
    data["positions"]=[]
    getGlobalFilters(data);
  }, []);

  // let dataForBarChart: any;
  // const getDataForChart = (item: any) => {

  //   dataForBarChart = [
  //     {
  //       visits: "Acheived",
  //       value:
  //         item.achieved_target,
  //     },
  //     {
  //       visits: "Target",
  //       value:
  //         item.target,
  //     }

  //   ];

  // }




  return (
    <DashboardWrapper>
      <>
        <div className="flex justify-between items-center px-6 ">
          <div className="flex flex-col gap-1 pl-1 pb-3 text-13 font-medium ">
            
            <p>{t("DashboardAnalytics.reportTitle")}</p>
            <p className="text-[20px] font-semibold">
              {timeFrame ? (
                <>
                  {timeFrame === "Today" && t("DashboardAnalytics.today")}
                  {timeFrame === "Yesterday" &&
                    t("timeframeselector.yesterday")}
                  {timeFrame === "Month Till Date" &&
                    t("DashboardAnalytics.monthtilldate")}
                </>
              ) : (
                startDate?.split("-").reverse().join("-") +
                " To " +
                endDate?.split("-").reverse().join("-")
              )}
            </p>
          </div>
          <div>
            <button
              className="flex items-center px-4 p-3 gap-2 bg-[#FAFAFA] border-2 border-[#EBEBEB]"
              onClick={() => setOpen(true)}
            >
              <img src={Filter} alt="" />{" "}
              {t("DashboardAnalytics.filterButton")}
            </button>
          </div>
        </div>
      </>
<div className="grid grid-cols-1 lg:grid-cols-2 gap-4 bg-[#F5F5F5] p-2 rounded">
            {/*  Active Customers & Payers */}
            <div className="bg-white p-4 border-2 flex flex-col rounded col-span-1 lg:col-span-2">
            <div className="mb-4">
              <h2 className="text-[13px] font-bold"> Active Customers</h2>
            </div>
            <div className="flex gap-4 items-stretch">
              <div className="w-full lg:w-3/4">
                <div className="grid grid-cols-3 grid-rows-2 gap-4">
                  {(orderMetrics?.customer_metrics?.channel_wise_active_customers?.filter((ch: any) => !isExcludedChannel(ch?.channel_name)) || [])?.map((ch: any, index: number) => {
                    const colors = [
                      "#FF4D68",
                      "#FFAA2A",
                      "#BE52F2",
                      "#408BFC",
                      "#00C48C",
                      "#00E5DE",
                      "#870091",
                      "#000000",
                    ];
                    const color = colors[index % colors.length];
                    // Find matching payer data for this channel
                    const payerData = orderMetrics?.customer_metrics?.channel_wise_active_payers?.find(
                      (payer: any) => payer.channel_id === ch.channel_id
                    );
                    const payerCount = payerData?.count || 0;
                    return (
                      <div key={ch.channel_id} className="border rounded p-3 flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <div className="h-3 w-3 rounded-full" style={{ backgroundColor: color }}></div>
                          <div className="text-[11px] font-semibold">{ch.channel_name}</div>
                        </div>
                        <div className="flex flex-col gap-1">
                          <div className="flex justify-between items-center">
                            <span className="text-[10px] text-gray-600">Outlets</span>
                            <span className="text-[14px] font-bold">{formatNumberWithoutFraction(ch.count || 0)}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-[10px] text-gray-600">Payers</span>
                            <span className="text-[14px] font-bold">{formatNumberWithoutFraction(payerCount)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="w-full lg:w-1/4 border-l-2 flex items-center justify-center">
                <div className="flex flex-col items-center gap-2 w-full">
                  <div className="flex items-center gap-4 w-full justify-center">
                    <div className="flex flex-col items-center">
                      <span className="text-[11px] font-semibold">Total Outlets</span>
                      <span className="text-[24px] font-bold text-green-600">
                        {formatNumberWithoutFraction(
                          orderMetrics?.customer_metrics?.total_outlet_count || 0
                        )}
                      </span>
                    </div>
                    <div className="h-12 border-l-2 border-gray-300"></div>
                    <div className="flex flex-col items-center">
                      <span className="text-[11px] font-semibold">Active Outlets</span>
                      <span className="text-[24px] font-bold text-green-600">
                        {formatNumberWithoutFraction(
                          orderMetrics?.customer_metrics?.unique_outlet_order_count || 0
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="w-full border-t-2 border-gray-300"></div>
                  <div className="flex items-center gap-4 w-full justify-center">
                    <div className="flex flex-col items-center">
                      <span className="text-[11px] font-semibold">Total Payers</span>
                      <span className="text-[24px] font-bold text-green-600">
                        {formatNumberWithoutFraction(
                          orderMetrics?.customer_metrics?.total_payer_count || 0
                        )}
                      </span>
                    </div>
                    <div className="h-12 border-l-2 border-gray-300"></div>
                    <div className="flex flex-col items-center">
                      <span className="text-[11px] font-semibold">Active Payers</span>
                      <span className="text-[24px] font-bold text-green-600">
                        {formatNumberWithoutFraction(
                          orderMetrics?.customer_metrics?.unique_payer_order_count || 0
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-full lg:w-1/4 border-l-2 flex flex-col items-start justify-start p-4">
                <div className="mb-4">
                  <h2 className="text-[13px] font-bold">Pending SAP Sync Orders</h2>
                </div>
                <div className="flex flex-col items-start gap-2 w-full">
                  <div className="flex flex-col items-start">
                    <span className="text-[11px] font-semibold">Orders</span>
                    <span className="text-[24px] font-bold text-green-600">
                      {formatNumberWithoutFraction(
                        orderMetrics?.orders_metrics?.pending_sap_sync_orders?.count || 0
                      )}
                    </span>
                  </div>
                  <div className="w-full border-t-2 border-gray-300"></div>
                  <div className="flex flex-col items-start">
                    <span className="text-[11px] font-semibold">Product Quantity</span>
                    <span className="text-[24px] font-bold text-green-600">
                      {formatNumberWithoutFraction(
                        orderMetrics?.orders_metrics?.pending_sap_sync_orders?.product_quantity  || 0
                      )}
                    </span>
                  </div>
                  
                </div>
              </div>
            </div>
          </div>
            {/* Orders Count Distribution Card */}
            <div className="bg-white border-2 p-4 flex flex-col rounded">
    <h2 className="text-[13px] font-semibold">
      Orders Count Distribution
    </h2>
    <div className="flex pt-2">
      <div className="flex flex-col w-[30%] gap-2">
        <div className="flex flex-col">
          <p className="font-semibold">
            <span className="text-[31px]">
              {formatNumberWithoutFraction(
                orderMetrics?.orders_metrics?.total_orders?.count || 0
              )}
            </span>
          </p>
          <p className="text-[13px] font-semibold">
            Total Orders
          </p>
        </div>
        <div className="flex flex-col">
          <p className="text-[#00C48C] text-[25px] font-bold">
            {formatNumberWithoutFraction(
              orderMetrics?.orders_metrics?.confirmed_orders?.count || 0
            )}
          </p>
          <p className="text-[13px] font-semibold">
            Confirmed Orders
          </p>
        </div>
        <div className="flex flex-col">
          <p className="text-[#408BFC] text-[25px] font-bold">
            {formatNumberWithoutFraction(
              orderMetrics?.orders_metrics?.delivered_orders?.count || 0
            )}
          </p>
          <p className="text-[13px] font-semibold">
            Delivered Orders
          </p>
        </div>
        <div className="flex flex-col">
          <p className="text-[#b84dff] text-[25px] font-bold">
            {formatNumberWithoutFraction(
              orderMetrics?.orders_metrics?.shipped_orders?.count || 0
            )}
          </p>
          <p className="text-[13px] font-semibold">
            Shipped Orders
          </p>
        </div>
        <div className="flex flex-col">
          <p className="text-[#FF4D68] text-[25px] font-bold">
            {formatNumberWithoutFraction(
              orderMetrics?.orders_metrics?.cancelled_orders?.count || 0
            )}
          </p>
          <p className="text-[13px] font-semibold">
            Cancelled Orders
          </p>
        </div>
      </div>
      <div className="h-[300px] w-[70%] flex justify-center">
        <PieChart 
          data={getPieChartData().countData}
        />
      </div>
    </div>
  </div>

          
            {/* Product Quantity Distribution Card */}
            <div className="bg-white border-2 p-4 flex flex-col rounded">
    <h2 className="text-[13px] font-semibold">
      Product Quantity Distribution
    </h2>
    <div className="flex pt-2">
      <div className="flex flex-col w-[30%] gap-2">
        <div className="flex flex-col">
          <p className="font-semibold">
            <span className="text-[31px]">
              {formatNumberWithoutFraction(
                orderMetrics?.orders_metrics?.total_orders?.product_quantity || 0
              )}
            </span>
          </p>
          <p className="text-[13px] font-semibold">
            Total Quantity
          </p>
        </div>
        <div className="flex flex-col">
          <p className="text-[#00C48C] text-[25px] font-bold">
            {formatNumberWithoutFraction(
              orderMetrics?.orders_metrics?.confirmed_orders?.product_quantity || 0
            )}
          </p>
          <p className="text-[13px] font-semibold">
            Confirmed Quantity
          </p>
        </div>
        <div className="flex flex-col">
          <p className="text-[#408BFC] text-[25px] font-bold">
            {formatNumberWithoutFraction(
              orderMetrics?.orders_metrics?.delivered_orders?.product_quantity || 0
            )}
          </p>
          <p className="text-[13px] font-semibold">
            Delivered Quantity
          </p>
        </div>
        <div className="flex flex-col">
          <p className="text-[#b84dff] text-[25px] font-bold">
            {formatNumberWithoutFraction(
              orderMetrics?.orders_metrics?.shipped_orders?.product_quantity || 0
            )}
          </p>
          <p className="text-[13px] font-semibold">
            Shipped Quantity
          </p>
        </div>
        <div className="flex flex-col">
          <p className="text-[#FF4D68] text-[25px] font-bold">
            {formatNumberWithoutFraction(
              orderMetrics?.orders_metrics?.cancelled_orders?.product_quantity || 0
            )}
          </p>
          <p className="text-[13px] font-semibold">
            Cancelled Quantity
          </p>
        </div>
      </div>
      <div className="h-[300px] w-[70%] flex justify-center">
        <PieChart 
          data={getPieChartData().quantityData}
        />
      </div>
    </div>
  </div>
    {/* Order Types Distribution Donut Chart */}
    <div className="bg-white border-2 p-4 flex flex-col rounded">
    <h2 className="text-[13px] font-semibold">
      Order Types Distribution
    </h2>
    <div className="flex pt-2">
      <div className="flex flex-col w-[30%] gap-2">
        <div className="flex flex-col">
          
          
        </div>
        <div className="flex flex-col">
          <p className="text-[#00C48C] text-[25px] font-bold">
            {formatNumberWithoutFraction(
              orderMetrics?.orders_metrics?.cod_orders?.count || 0
            )}
          </p>
          <p className="text-[13px] font-semibold">
            COD Orders
          </p>
        </div>
        <div className="flex flex-col">
        <p className="text-[#408BFC] text-[25px] font-bold">
            {formatNumberWithoutFraction(
              orderMetrics?.orders_metrics?.credit_orders?.count || 0
            )}
          </p>
          <p className="text-[13px] font-semibold">
            Credit Orders
          </p>
        </div>
        <div className="flex flex-col">
        <p className="text-[#b84dff] text-[25px] font-bold">
            {formatNumberWithoutFraction(
              orderMetrics?.orders_metrics?.paid_orders?.count || 0
            )}
          </p>
          <p className="text-[13px] font-semibold">
            Paid Orders
          </p>
        </div>
        {/* <div className="flex flex-col">
        <p className="text-[#FF4D68] text-[25px] font-bold">
            {formatNumberWithoutFraction(
              orderMetrics?.orders_metrics?.pending_payment_orders?.count || 0
            )}
          </p>
          <p className="text-[13px] font-semibold">
            Pending Payment Orders
          </p>
        </div> */}

        
      </div>
      <div className="h-[300px] w-[70%] flex justify-center">
        <PieChart 
          data={getOrderTypesChartData()}
        />
      </div>
    </div>
  </div>

            <div className="bg-white p-4 border-2 flex flex-col rounded">
            <h2 className="text-[13px] font-bold mb-4"> PO Orders</h2>
            <div className="h-[200px] w-full mb-4">
              <BarChart
                data={dataForBarChartOrders}
                barColors={[
                  "#FF4D68",
                  "#FFAA2A",
                  "#BE52F2",
                  "#408BFC",
                  "#00C48C",
                  "#00E5DE",
                  "#870091",
                  "#000000",
                ]}
                xAxisLabel="Channels"
                yAxisLabel="Orders"
              />
            </div>
            <div className="flex flex-col justify-center gap-2">
              {filteredChannelsWiseOrders?.map((channel: any, index: number) => {
                const colors = [
                  "#FF4D68",
                  "#FFAA2A", 
                  "#BE52F2",
                  "#408BFC",
                  "#00C48C",
                  "#00E5DE",
                  "#870091",
                  "#000000"
                ];
                const color = colors[index % colors.length];
                
                return (
                  <div key={channel.channel_id} className="flex justify-between">
                    <div className="flex gap-2">
                      <div
                        className="h-4 w-4 rounded-full"
                        style={{ backgroundColor: color }}
                      ></div>
                      <span className="text-[13px]">{channel.channel_name}</span>
                    </div>
                    <span className="ml-2 text-base">
                      {formatNumberWithoutFraction(channel.count || 0)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="bg-white p-4 border-2 flex flex-col rounded">
            <h2 className="text-[13px] font-bold mb-4"> Product Quantity</h2>
            <div className="h-[200px] w-full mb-4">
              <BarChart
                data={dataForBarChartCartons}
                barColors={[
                  "#FF4D68",
                  "#FFAA2A",
                  "#BE52F2",
                  "#408BFC",
                  "#00C48C",
                  "#00E5DE",
                  "#870091",
                  "#000000",
                ]}
                xAxisLabel="Channels"
                yAxisLabel="Cartons"
              />
            </div>
            <div className="flex flex-col justify-center gap-2">
              {filteredChannelsWiseOrders?.map((channel: any, index: number) => {
                const colors = [
                  "#FF4D68",
                  "#FFAA2A", 
                  "#BE52F2",
                  "#408BFC",
                  "#00C48C",
                  "#00E5DE",
                  "#870091",
                  "#000000"
                ];
                const color = colors[index % colors.length];
                
                return (
                  <div key={channel.channel_id} className="flex justify-between">
                    <div className="flex gap-2">
                      <div
                        className="h-4 w-4 rounded-full"
                        style={{ backgroundColor: color }}
                      ></div>
                      <span className="text-[13px]">{channel.channel_name}</span>
                    </div>
                    <span className="ml-2 text-base">
                      {formatNumberWithoutFraction(channel.total_carton_qty || 0)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          
</div>


      {open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-end sm:items-start items-center z-50">
          <div className="bg-white shadow-lg w-full max-w-md h-full">
            <div className="flex justify-between p-6 border-b-2">
              <div className="text-16 font-bold">
                {t("DashboardAnalytics.filtersTitle")}
              </div>
              <div>
                <img
                  className="cursor-pointer"
                  onClick={() => setOpen(false)}
                  src={Close}
                  alt=""
                />
              </div>
            </div>
            <div className="flex flex-col justify-between h-[calc(100vh-100px)] px-12 pt-6  overflow-y-auto">
              <div className="relative flex flex-col gap-[30px]">
                <div className="absolute z-30 top-[50%] left-[45%]">
                  {loadingFilter && <Loader />}
                </div>
                <div className="flex flex-col sm:flex-row gap-4 w-full">
                    <div className="w-1/2">
                      <DropdownMulti
                        label={t("visitreports.filters.region")}
                        options={globalFilters?.regions.map((sales: any) => ({
                          value: sales,
                          label: sales,
                        }))}
                        selectedValues={salesRegion}
                        onClose={handleFiltersUpdate}
                        onChange={setSelectedSalesRegion}
                        placeholder={t("visitreports.filters.selectRegion")}
                      />
                    </div>
                    <div className="w-1/2">
                      <DropdownMulti
                        label={t("visitreports.filters.salesOffice")}
                        options={
                          globalFilters?.sales_offices.map((sales: any) => ({
                            value: sales.id,
                            label: sales.name,
                          })) || []
                        }
                        selectedValues={salesOffice}
                        onClose={handleFiltersUpdate}
                        onChange={setSelectedSalesOffice}
                        disabled={salesRegion.length === 0 || loadingFilter}
                        placeholder={t(
                          "visitreports.filters.selectSalesOffice"
                        )}
                      />
                    </div>
                  </div>
                <div className="date-range-picker space-y-2">
                  <div className="flex gap-4 w-full">
                    <div className="w-1/2">
                      <label className="block text-10 text-gray-500 pb-1">Year</label>
                      <Dropdown
                        label=""
                        options={dateRangeSelector.getMonthYearOptions()}
                        selectedValue={dateRangeSelector.selectedMonthYear}
                        onChange={handleMonthYearDropdownChange}
                        placeholder="Select Year"
                      />
                    </div>
                    <div className="w-1/2">
                      <label className="block text-10 text-gray-500 pb-1">Month</label>
                      <Dropdown
                        label=""
                        options={dateRangeSelector.getAvailableMonths()}
                        selectedValue={dateRangeSelector.selectedMonth ? Number(dateRangeSelector.selectedMonth) : ""}
                        onChange={handleMonthDropdownChange}
                        placeholder="Select Month"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4 w-full">
                    <div className="w-1/2">
                      <label className="block text-10 text-gray-500 pb-1">
                        {t("DashboardAnalytics.startDateLabel")}
                      </label>
                      <input
                        type="date"
                        className={`rounded p-2 border-2 border-gray-400 w-full ${dateMode === "month" ? "bg-gray-100 text-gray-400 cursor-not-allowed opacity-70" : ""}`}
                        value={startDate}
                        onChange={handleStartDateChange}
                        onKeyDown={(e) => e.preventDefault()}
                        max={new Date(endDate).toISOString().split("T")[0]}
                        min={(() => {
                          const end = new Date(endDate);
                          const minDate = new Date(end);
                          minDate.setDate(end.getDate() - 30);
                          return minDate > new Date(0) ? minDate.toISOString().split("T")[0] : '1970-01-01';
                        })()}
                        disabled={dateMode === "month"}
                      />
                    </div>
                    <div className="w-1/2">
                      <label className="block text-10 text-gray-500 pb-1">
                        {t("DashboardAnalytics.endDateLabel")}
                      </label>
                      <input
                        type="date"
                        className={`rounded p-2 border-2 border-gray-400 w-full ${dateMode === "month" ? "bg-gray-100 text-gray-400 cursor-not-allowed opacity-70" : ""}`}
                        value={endDate}
                        onChange={handleEndDateChange}
                        onKeyDown={(e) => e.preventDefault()}
                        max={new Date().toISOString().split("T")[0]}
                        min={new Date(startDate).toISOString().split("T")[0]}
                        disabled={dateMode === "month"}
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <TimeFrameSelector
                    customClass="w-full border border-gray-300 rounded p-2"
                    setSelectTimeFrame={(tf) => {
                      setDateMode("picker");
                      dateRangeSelector.setSelectedMonthYear("");
                      dateRangeSelector.setSelectedMonth("");
                      setSelectTimeFrame(tf);
                    }}
                    timeFrame={timeFrame}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                  />
                </div>
              </div>

              <div className="flex justify-between mt-4">
                <button
                  onClick={handleClearFilters}
                  className="px-4 py-2 text-black bg-gray-200 rounded transition duration-200 hover:bg-gray-300"
                >
                  {t("DashboardAnalytics.clearFilterButton")}
                </button>
                <button
                  onClick={() => handleApply()}
                  className="px-4 py-2 text-white bg-purple rounded transition duration-200 hover:bg-[#F3E6F4] hover:text-purple"
                >
                  {t("DashboardAnalytics.applyFilterButton")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardWrapper>
  )
}

export default B2BDashboard